import { useEffect, useMemo, useRef, useState } from "react";
import { connectHubUrl } from "./connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { authedHub, setHubAccessToken } from "./sharedHub";

export const ConnectEvents = {
    ChannelMessageSent: "channel.message.sent",
    ChannelMessageEdited: "channel.message.edited",
    ChannelMessageDeleted: "channel.message.deleted",
    ChannelMessagePinned: "channel.message.pinned",
    ChannelReaction: "channel.message.reaction",
    TypingStarted: "channel.typing.started",
    TypingStopped: "channel.typing.stopped",
    PresenceChanged: "presence.changed",
    DirectMessageSent: "dm.message.sent",
    /** A 1:1 or group chat changed for me: message, edit, delete, reaction or read. */
    ChatUpdated: "chat.updated",
    /** Someone is typing in one of my 1:1 or group chats. */
    ChatTyping: "chat.typing",
    /** A recording of a meeting I'm part of was saved to Drive. */
    MeetingRecordingAdded: "meeting.recording.added",
    /** A poll in one of my conversations got a vote, was closed or removed. */
    PollUpdated: "poll.updated"
} as const;

export type ConnectEventName = (typeof ConnectEvents)[keyof typeof ConnectEvents];

/**
 * The page's ONE /hubs/connect connection, shared by the shell and the incoming-call overlay (each used to
 * open its own, and both were rebuilt whenever the shell remounted or the token changed — 6–8 live
 * connections per user on production). A short grace keeps it across a remount.
 */
export const connectHub = authedHub(connectHubUrl, { graceMs: 4000 });

interface UseConnectHubOptions {
    /** Hub does not connect while false (and until a token is available). */
    enabled?: boolean;
    /** Listener map — fresh copy per render is fine. */
    on?: Partial<Record<ConnectEventName, (payload: unknown) => void>>;
}

/**
 * Subscribes the component to the shared ConnectHub connection. Exposes `joinChannel` / `typing` /
 * `setStatus` and the current connection state so the UI can show a "connecting…" banner.
 */
export function useConnectHub({ enabled = true, on }: UseConnectHubOptions = {}) {
    const token = useAccessToken();
    const listenersRef = useRef(on ?? {});
    listenersRef.current = on ?? {};
    const [snapshot, setSnapshot] = useState(connectHub.snapshot);

    // Declared before the acquire effect so the token is in place when it connects.
    useEffect(() => { setHubAccessToken(token); }, [token]);

    useEffect(() => {
        if (!enabled) return;
        connectHub.acquire();
        const offs = Object.values(ConnectEvents).map((name) =>
            connectHub.on(name, (payload: unknown) => { listenersRef.current[name]?.(payload); }));
        const offState = connectHub.subscribe(() => setSnapshot(connectHub.snapshot));
        setSnapshot(connectHub.snapshot);
        return () => {
            for (const off of offs) off();
            offState();
            connectHub.release();
        };
    }, [enabled]);

    const api = useMemo(
        () => ({
            joinChannel: (channelId: string) => connectHub.current()?.invoke("JoinChannel", channelId),
            leaveChannel: (channelId: string) => connectHub.current()?.invoke("LeaveChannel", channelId),
            typing: (channelId: string, started: boolean) => connectHub.current()?.invoke("Typing", channelId, started),
            typingInChat: (kind: "dm" | "group", chatId: string, started: boolean) =>
                connectHub.current()?.invoke("TypingInChat", kind, chatId, started),
            setStatus: (status: string) => connectHub.current()?.invoke("SetStatus", status)
        }),
        []
    );

    // MUST be memoised. A fresh object per render re-ran every consumer effect with `hub` in its deps — the
    // presence effect then called SetStatus, the echo re-rendered, and so on (~60 invocations/s per tab).
    return useMemo(() => ({ state: snapshot.state, ...api }), [snapshot.state, api]);
}
